/**
 * Shift-notification helper — shows a sticky local notification while a CSA
 * is on an active shift. The notification's tap-target opens the CSA portal
 * directly on the Online tab (matching the `hisabx://csa?tab=online&quick=1`
 * deep link registered by the App Shortcut).
 *
 * Silently no-ops on web / PWA — only fires inside the Capacitor Android app.
 *
 * Usage:
 *   import { showShiftNotification, clearShiftNotification } from "@/lib/shiftNotification";
 *   // when check-in succeeds:
 *   showShiftNotification({ shiftName: "Morning", pumpName: "Demo Pump" });
 *   // when check-out / shift close succeeds:
 *   clearShiftNotification();
 */

const NOTIFICATION_ID = 1701; // Fixed ID so a re-show replaces the previous notification
const CHANNEL_ID = "hisabx_active_shift";

async function loadPlugin() {
  try {
    const cap = await import("@capacitor/core").catch(() => null);
    if (!cap?.Capacitor?.isNativePlatform?.()) return null;
    const mod = await import("@capacitor/local-notifications").catch(() => null);
    return mod?.LocalNotifications || null;
  } catch {
    return null;
  }
}

async function ensureChannelAndPermission(LN) {
  try {
    // Permission — first-time only on Android 13+. Idempotent.
    const p = await LN.checkPermissions();
    if (p.display !== "granted") {
      const req = await LN.requestPermissions();
      if (req.display !== "granted") return false;
    }
  } catch { /* older Android — no permission dialog needed */ }
  try {
    await LN.createChannel({
      id: CHANNEL_ID,
      name: "Active shift",
      description: "Shows while you are on shift so you can quickly add online transactions",
      importance: 3,          // IMPORTANCE_DEFAULT — visible but not intrusive
      visibility: 1,           // VISIBILITY_PUBLIC — shows on lockscreen
      sound: null,
      vibration: false,
      lights: false,
    });
  } catch { /* channel may already exist */ }
  return true;
}

export async function showShiftNotification({ shiftName, pumpName, shiftEndAt }) {
  const LN = await loadPlugin();
  if (!LN) return;
  const ok = await ensureChannelAndPermission(LN);
  if (!ok) return;
  try {
    // Base sticky notification — "you're on shift"
    const notifications = [{
      id: NOTIFICATION_ID,
      title: `On shift · ${shiftName || "Active"}`,
      body: `Tap to add an online transaction${pumpName ? ` · ${pumpName}` : ""}`,
      channelId: CHANNEL_ID,
      ongoing: true, autoCancel: false,
      smallIcon: "ic_stat_icon",
      extra: { deeplink: "hisabx://csa?tab=online&quick=1" },
      actionTypeId: "SHIFT_ACTIONS",
    }];
    // Add 3 shift-end reminders if we know the end time.
    // At: 30 min before end · at end · 90 min after end (30 min before 2hr cutoff).
    if (shiftEndAt) {
      const end = new Date(shiftEndAt).getTime();
      const now = Date.now();
      const reminders = [
        { offset: -30 * 60_000, id: 1702, title: "Shift ending soon", body: "30 min left — start wrapping up your HISAB." },
        { offset: 0,             id: 1703, title: "Time to close shift", body: "Save remaining readings and log online txns." },
        { offset: 90 * 60_000,  id: 1704, title: "30 min left to submit!", body: "Submission window closes in 30 min. Ask manager to unlock if needed." },
      ];
      for (const r of reminders) {
        const at = end + r.offset;
        if (at <= now) continue;
        notifications.push({
          id: r.id, title: r.title, body: r.body, channelId: CHANNEL_ID,
          schedule: { at: new Date(at), allowWhileIdle: true },
          smallIcon: "ic_stat_icon",
          extra: { deeplink: "hisabx://csa?tab=close" },
        });
      }
    }
    await LN.schedule({ notifications });
  } catch (e) {
    console.warn("shift notification failed", e);
  }
}

export async function clearShiftNotification() {
  const LN = await loadPlugin();
  if (!LN) return;
  try {
    await LN.cancel({ notifications: [
      { id: NOTIFICATION_ID }, { id: 1702 }, { id: 1703 }, { id: 1704 },
    ] });
  } catch { /* ignore */ }
}

/**
 * Register global action-tap handler once at app boot.
 * When the user taps the notification body (or a future action button),
 * we navigate to the deep link stored in `extra.deeplink`.
 */
export async function registerNotificationHandlers(navigate) {
  const LN = await loadPlugin();
  if (!LN) return () => {};
  try {
    // Register the action set so future notifications can add buttons if needed
    await LN.registerActionTypes({
      types: [{
        id: "SHIFT_ACTIONS",
        actions: [
          { id: "add_online", title: "Add online txn" },
        ],
      }],
    });
    const sub = await LN.addListener("localNotificationActionPerformed", (evt) => {
      const deeplink = evt?.notification?.extra?.deeplink;
      if (deeplink && deeplink.startsWith("hisabx://")) {
        const dest = deeplink.replace(/^hisabx:\/\//, "/");
        navigate(dest);
      }
    });
    return () => sub.remove?.();
  } catch (e) {
    console.warn("notification handler register failed", e);
    return () => {};
  }
}
